// AC SIZE CALCULATOR: the words, the sizing rule and the guides.
//
// Shared by pages/ACCalculator.jsx (the live page) and scripts/prerender.js
// (the HTML Google reads before any JavaScript runs). Both must say the same
// thing, so the copy and the numbers live here once.
//
// No imports on purpose: prerender.js loads this file by stripping the
// `export` keywords and evaluating it, which only works for a file that
// stands alone. What it needs is passed in by the caller:
//   engine = { ...lib/heatLoad.js, STATIONS, cityFor, TARIFF (data/indiaClimate.js),
//              calcEnergy (pages/ac-engine.js) }
//   units  = unitsFrom(CATALOGUE, SERIES): every model with its real kW and
//            the outdoor temperature it keeps full power to.
//
// Every number in the guides and the FAQ is worked out by the same engine the
// calculator runs. Nothing is typed in by hand, so the page can never quote a
// size the calculator would not give.

export const CALC_SLUG = 'ac-calculator';

export const CALC_SEO = {
  title: 'AC Size Calculator: What Ton AC Do I Need? | Citi Cool',
  meta:
    "Free AC tonnage calculator for Delhi and 50+ Indian cities. Daikin's heat-load method, sized for your city's " +
    "hottest afternoon, with matching ACs at today's prices.",
  h1: 'AC size calculator: what ton AC does your room need?',
  intro:
    "Six quick questions. We work out the heat load line by line the way Daikin's engineers do, for the hottest afternoon " +
    'of a normal year in your city, then show only the ACs that still have the power at that heat.',
};

// ─── units and labels ─────────────────────────────────────────────────────

// Every model the shop lists, as the sizing sees it.
export function unitsFrom(catalogue, series) {
  return catalogue.map((m) => ({
    id: m.id,
    series: m.series,
    soldAs: Number(m.soldAs),
    kw: m.kw,
    full: (series[m.series] && series[m.series].heat && series[m.series].heat.full) || 43,
    inverter: Boolean(m.inverter),
  }));
}

export const tonLabel = (s) => `${Number(s).toFixed(1)} Ton`;
export const degC = (n) => `${(Math.round(n * 10) / 10).toFixed(1)}°C`;
const tr2 = (n) => `${n.toFixed(2)} TR`;

// "FTKU, GTKM and JTKJ"
export function listSeries(units) {
  const names = [...new Set(units.map((u) => u.series))];
  if (names.length < 2) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

// ─── the sizing rule ──────────────────────────────────────────────────────
//
// The smallest size in which at least one AC still covers the room on the
// hottest afternoon (after it loses power to the heat) and in the monsoon.
// Above the biggest split we sell: two ACs. Above 3.5 TR in one room: our
// engineers (cassette, ductable or VRV).

export const TEAM_ABOVE_TR = 3.5;

export function answerFor(engine, need, units) {
  if (need.loadTR > TEAM_ABOVE_TR) return { kind: 'team' };
  const sizes = [...new Set(units.map((u) => u.soldAs))].sort((a, b) => a - b);
  for (let i = 0; i < sizes.length; i += 1) {
    const size = sizes[i];
    const all = units.filter((u) => u.soldAs === size);
    const ok = all.filter((u) => engine.fits(u, need));
    if (!ok.length) continue;
    const short = all.filter((u) => !ok.includes(u));
    const restricted = short.length > 0;
    // Only some models of this size keep up. Is it because they keep full power to a higher temperature?
    const byHeat = restricted && Math.min(...ok.map((u) => u.full)) > Math.max(...short.map((u) => u.full));
    const next = sizes[i + 1];
    const okNext = restricted && next != null ? units.filter((u) => u.soldAs === next && engine.fits(u, need)) : [];
    return {
      kind: 'single', size, ok, short, restricted, byHeat,
      ratedC: byHeat ? Math.min(...ok.map((u) => u.full)) : null,
      next: okNext.length ? next : null,
      okNext,
    };
  }
  return { kind: 'pair' };
}

export function answerLabel(ans) {
  if (ans.kind === 'team') return 'Central system';
  if (ans.kind === 'pair') return 'Two ACs';
  return tonLabel(ans.size);
}

// One room's load. `l` and `w` in feet, or `area` alone (taken as a square).
export function roomNeed(engine, {
  l, w, area, floor, sun, type = 'bedroom', people, city = 'delhi', height = 10, tC = 24, extra,
}) {
  const side = area ? Math.sqrt(area) : null;
  const c = engine.cityFor(city);
  const climate = engine.climateFor(c.station);
  const room = engine.roomFor({ type, l: l || side, w: w || side, floor, sun, people, height, ...(extra || {}) });
  return { ...engine.needFor(room, climate, engine.insideAt(tC)), climate, city: c, room };
}

// ─── room-size guide (the table) ──────────────────────────────────────────

export const GUIDE_COLUMNS = [
  { key: 'shade', label: 'Shaded, middle floor', floor: 'mid', sun: 'N' },
  { key: 'sun', label: 'Afternoon sun, middle floor', floor: 'mid', sun: 'W' },
  { key: 'top', label: 'Afternoon sun, top floor', floor: 'top', sun: 'W' },
];

export const GUIDE_ROOMS = [
  [10, 10], [10, 12], [12, 12], [12, 14], [12, 15], [14, 14], [15, 15], [16, 16], [15, 20],
];

export function guideNote(engine) {
  const c = engine.climateFor(engine.STATIONS.delhi);
  return `Bedroom with two people in Delhi, room held at 24°C, on a ${degC(c.peakC)} afternoon, the hottest day of an average year. ` +
    'Each size is the smallest that still covers the room at that heat, after the AC loses power to it. ' +
    'Living rooms, offices and kitchens need more: use the calculator for those.';
}

// A cell's small print: which models of the size keep up, when not all do.
export function cellNote(ans) {
  if (ans.kind !== 'single' || !ans.restricted) return '';
  if (ans.byHeat) return `Models rated to ${ans.ratedC}°C`;
  const okSeries = new Set(ans.ok.map((u) => u.series)).size;
  return okSeries <= 2 ? `${listSeries(ans.ok)} only` : `All but ${listSeries(ans.short)}`;
}

export function sizeGuide(engine, units) {
  return GUIDE_ROOMS.map(([l, w]) => ({
    room: `${l} × ${w} ft`,
    area: l * w,
    cells: GUIDE_COLUMNS.map((c) => {
      const need = roomNeed(engine, { l, w, floor: c.floor, sun: c.sun });
      const ans = answerFor(engine, need, units);
      return {
        key: c.key, tr: need.loadTR, trText: tr2(need.loadTR), label: answerLabel(ans),
        size: ans.kind === 'single' ? ans.size : null, note: cellNote(ans),
      };
    }),
  }));
}

// ─── the same room across India ───────────────────────────────────────────

export const CITY_GUIDE = [
  'delhi', 'jaipur', 'lucknow', 'ahmedabad', 'nagpur', 'patna', 'bhopal', 'amritsar',
  'hyderabad', 'kolkata', 'chennai', 'mumbai', 'pune', 'bengaluru', 'guwahati',
];

export const CITY_GUIDE_NOTE =
  '12 × 12 ft bedroom (144 sq ft), two people, window with afternoon sun, room held at 24°C. ' +
  'Hottest afternoon: the hottest day of an average year (IMD), or the IS 7896 design temperature plus 3°C where IMD does not publish it.';

export function cityGuide(engine, units) {
  return CITY_GUIDE.map((key) => {
    const cell = (floor) => {
      const need = roomNeed(engine, { l: 12, w: 12, floor, sun: 'W', city: key });
      const ans = answerFor(engine, need, units);
      return { tr: need.loadTR, trText: tr2(need.loadTR), label: answerLabel(ans), size: ans.kind === 'single' ? ans.size : null, note: cellNote(ans) };
    };
    const c = engine.cityFor(key);
    const cl = engine.climateFor(c.station);
    return {
      key, city: c.label, peakC: cl.peakC, peakText: degC(cl.peakC), peakSource: cl.peakMeasured ? 'IMD' : 'Estimate',
      mid: cell('mid'), top: cell('top'),
    };
  });
}

// ─── how it works ─────────────────────────────────────────────────────────

export function howItWorks(engine) {
  const stations = Object.keys(engine.STATIONS).length;
  const delhi = engine.climateFor(engine.STATIONS.delhi);
  return [
    {
      title: 'Line by line, the Daikin way',
      text:
        "We follow Daikin's own heat-load sheet: sun through the glass, heat through the walls, roof and floor, people, lights, " +
        'gadgets and fresh air, each with the same factors and the same safety margins.',
    },
    {
      title: "Your city's hottest afternoon",
      text:
        `An AC has to hold the room on the hottest day of a normal year, not an average one. For ${stations} Indian weather stations we use the ` +
        `Bureau of Indian Standards design data (IS 7896) and IMD climate records. In Delhi that day reaches ${degC(delhi.peakC)}.`,
    },
    {
      title: 'The monsoon too',
      text: 'Humid monsoon days are cooler but carry far more moisture for the AC to take out. We work out both and size for the bigger.',
    },
    {
      title: 'Rooms around yours',
      text:
        'We take the average case: half the inside walls, floor and ceiling face rooms that are not air-conditioned, ' +
        'which is how most homes are used.',
    },
    {
      title: "The AC's own limit",
      text:
        'Every AC loses power when it is very hot outside. Daikin models keep full power up to 43°C or 46°C, depending on the series; ' +
        'above that we take off 5% for every degree. We only show ACs that still cover the room at your hottest afternoon.',
    },
    {
      title: 'Then the right size',
      text: 'The answer is the smallest size we sell that still keeps up. 1 TR is 12,000 BTU/hr of cooling.',
    },
  ];
}

// ─── FAQ ──────────────────────────────────────────────────────────────────

const inr = (n) => `₹${Math.round(n).toLocaleString('en-IN')}`;

export function calcFaq(engine, units) {
  const need = (args) => roomNeed(engine, args);
  const ans = (n) => answerFor(engine, n, units);
  const say = (a) => {
    if (a.kind === 'team') return 'a central system (cassette, ductable or VRV)';
    if (a.kind === 'pair') return 'two ACs';
    if (a.byHeat) return `a ${tonLabel(a.size)} AC rated to ${a.ratedC}°C${a.next ? `, or a ${tonLabel(a.next)}` : ''}`;
    return `a ${tonLabel(a.size)} AC`;
  };
  const delhi = engine.climateFor(engine.STATIONS.delhi);
  const peak = delhi.peakC;

  const r144 = [need({ l: 12, w: 12, floor: 'mid', sun: 'N' }), need({ l: 12, w: 12, floor: 'mid', sun: 'W' }), need({ l: 12, w: 12, floor: 'top', sun: 'W' })];
  const top144 = [need({ l: 12, w: 12, floor: 'top', sun: 'N' }), r144[2]];
  const r200 = [need({ area: 200, floor: 'mid', sun: 'N' }), need({ area: 200, floor: 'mid', sun: 'W' }), need({ area: 200, floor: 'top', sun: 'W' })];
  const midW = need({ l: 12, w: 14, floor: 'mid', sun: 'W' });
  const topW = need({ l: 12, w: 14, floor: 'top', sun: 'W' });
  const topPct = Math.round((topW.loadTR / midW.loadTR - 1) * 100);
  const bigLiving = need({ l: 20, w: 20, floor: 'top', sun: 'W', type: 'living' });
  const mumbai = need({ l: 12, w: 12, floor: 'mid', sun: 'W', city: 'mumbai' });
  const blr = need({ l: 12, w: 12, floor: 'mid', sun: 'W', city: 'bengaluru' });

  // Real capacities of what we sell.
  const one = units.filter((u) => u.soldAs === 1);
  const oneMax = Math.max(...one.map((u) => u.kw)) / engine.KW_PER_TR;
  const one43 = one.filter((u) => u.full <= 43);
  const one43kw = one43.length ? Math.max(...one43.map((u) => u.kw)) : 3.52;
  const stations = Object.keys(engine.STATIONS).length;
  const ton15 = units.filter((u) => u.soldAs === 1.5);
  const kws = ton15.map((u) => u.kw);
  const kwLow = kws.length ? Math.min(...kws) : 5.0;
  const kwHigh = kws.length ? Math.max(...kws) : 5.28;
  const kwText = (x) => (Number.isInteger(x) ? x.toFixed(1) : String(x));
  const at = (kw, full, t) => engine.capacityTR(kw, full, t);
  const biggest = Math.max(...units.map((u) => u.soldAs));

  // 3 Star vs 5 Star for a 12×12 Delhi bedroom with afternoon sun, on its load.
  const st = engine.STATIONS.delhi;
  const bill = (iseer, hrs) => engine.calcEnergy({
    ton: r144[1].loadTR, iseer, type: 'inverter', dailyHrs: hrs, coolingMonths: st.mo, elecRate: engine.TARIFF.home,
    targetTempC: 24, topFloor: false, highSun: true, poorInsul: false,
  }).yearly;
  const save8 = bill(4.4, 8) - bill(5.7, 8);
  const save16 = bill(4.4, 16) - bill(5.7, 16);

  // Is a 1.5 ton enough for 200 sq ft? Say it plainly, from the numbers.
  const fits15 = r200.map((n) => {
    const a = ans(n);
    return a.kind === 'single' && a.size <= 1.5;
  });
  let verdict200;
  if (fits15.every(Boolean)) verdict200 = 'Yes, for a bedroom.';
  else if (fits15[0] && fits15[1]) verdict200 = 'Yes on a middle floor, but not on a top floor with afternoon sun.';
  else if (fits15[0]) verdict200 = 'Only if the window is shaded.';
  else verdict200 = 'No, it needs a bigger AC.';

  const oneTon = top144.every((n) => n.summerTR > oneMax);

  return [
    {
      q: 'What size AC do I need for a 12×12 room?',
      a:
        `A 12×12 room is 144 sq ft. In Delhi, as a bedroom on a middle floor, it needs ${tr2(r144[0].loadTR)} with a shaded window, so ${say(ans(r144[0]))}, ` +
        `and ${tr2(r144[1].loadTR)} with afternoon sun, so ${say(ans(r144[1]))}. On a top floor with afternoon sun it needs ${tr2(r144[2].loadTR)}: ${say(ans(r144[2]))}.`,
    },
    {
      q: 'Will a 1 ton AC cool a 144 sq ft room on the top floor?',
      a:
        `${oneTon ? 'No.' : 'Only just.'} On a ${degC(peak)} Delhi afternoon that room needs ${tr2(top144[0].summerTR)} with a shaded window ` +
        `and ${tr2(top144[1].summerTR)} with afternoon sun. The most a 1 ton AC gives is ${tr2(oneMax)}, and a model rated to 43°C drops to ` +
        `${tr2(at(one43kw, 43, peak))} at that heat. The roof slab is the difference: it bakes all day and keeps giving off heat into the night. ` +
        `Get ${say(ans(top144[1]))}.`,
    },
    {
      q: 'Is a 1.5 ton AC enough for 200 sq ft?',
      a:
        `${verdict200} In Delhi a 200 sq ft bedroom on a middle floor needs ${tr2(r200[0].loadTR)} with a shaded window (${say(ans(r200[0]))}) ` +
        `and ${tr2(r200[1].loadTR)} with afternoon sun (${say(ans(r200[1]))}). On a top floor with afternoon sun it rises to ${tr2(r200[2].loadTR)}, ` +
        `which needs ${say(ans(r200[2]))}. A living room of the same size needs more, because more people use it.`,
    },
    {
      q: 'Why does an AC lose cooling in a heatwave?',
      a:
        'An AC pushes the heat it takes out of the room into the outside air. The hotter that air, the harder it gets. ' +
        `Daikin models give full power up to 43°C or 46°C, depending on the series, and lose about 5% for every degree above. ` +
        `On a ${degC(peak)} Delhi afternoon a ${kwText(kwLow)} kW (1.5 ton) AC rated to 43°C gives ${tr2(at(kwLow, 43, peak))}, ` +
        `while one rated to 46°C still gives its full ${tr2(at(kwLow, 46, peak))}. At 48°C they give ${tr2(at(kwLow, 43, 48))} and ${tr2(at(kwLow, 46, 48))}.`,
    },
    {
      q: 'How much bigger does the AC need to be on a top floor?',
      a:
        `For a 12×14 bedroom with afternoon sun in Delhi, about ${topPct}% bigger: ${tr2(midW.loadTR)} on a middle floor, ${tr2(topW.loadTR)} on the top floor. ` +
        `That is ${say(ans(midW))} against ${say(ans(topW))}.`,
    },
    {
      q: 'Does the city change the AC size?',
      a:
        `Yes. The same 12×12 bedroom with afternoon sun on a middle floor needs ${tr2(r144[1].loadTR)} in Delhi (${say(ans(r144[1]))}), ` +
        `${tr2(mumbai.loadTR)} in Mumbai (${say(ans(mumbai))}) and ${tr2(blr.loadTR)} in Bengaluru (${say(ans(blr))}). ` +
        `Pick your city in the calculator: it covers ${stations} weather stations across India.`,
    },
    {
      q: 'Is a 5 star AC worth the extra cost?',
      a:
        `It depends on how many hours you run it. For a 12×12 Delhi bedroom with afternoon sun, at ₹${engine.TARIFF.home} a unit, a 5 Star saves about ${inr(save8)} a year over a 3 Star ` +
        `at 8 hours a day, and about ${inr(save16)} a year at 16 hours. The calculator divides the price gap of the actual models in stock by that saving, ` +
        'and tells you how many years the 5 Star takes to pay for itself.',
    },
    {
      q: 'When does a room need two ACs?',
      a:
        `When no single split AC we sell covers it on the hottest afternoon: the biggest is ${tonLabel(biggest)}, and one unit cannot spread more cooling than that evenly. ` +
        `A 20×20 living room on a top floor with afternoon sun in Delhi, for example, needs ${tr2(bigLiving.loadTR)}. ` +
        `Above ${TEAM_ABOVE_TR} TR in one room, a cassette, ductable or VRV system is the better fit, and our engineers design those.`,
    },
    {
      q: 'What does TR (ton) mean on an AC?',
      a:
        'One ton of refrigeration (1 TR) is 12,000 BTU per hour, or 3.517 kW of cooling. It has nothing to do with weight. ' +
        `The "1.5 ton" on the box is a class: Daikin's 1.5 ton models deliver ${kwText(kwLow)} to ${kwText(kwHigh)} kW, which is ` +
        `${(kwLow / engine.KW_PER_TR).toFixed(2)} to ${(kwHigh / engine.KW_PER_TR).toFixed(2)} TR. The calculator uses the real figure, not the label.`,
    },
    {
      q: 'Why does the calculator round up instead of down?',
      a:
        'An AC can only remove as much heat as its capacity. If a room needs 1.2 TR and gets an AC that gives 1.0, it never reaches 24°C on the hottest afternoons. ' +
        'An inverter AC slows down once the room is cool, so a little spare capacity costs very little to run.',
    },
  ];
}
